import { ReactNode, useEffect, useMemo, useState } from "react";
import { InputLabel } from "@sellar-io/components";
import { usePriceTierList } from "../../../hooks/api/v2/priceTier/usePriceTierList";
import {
  RadioGroup,
  RadioGroupOption,
  RadioGroupContainer,
} from "../../../components/radioSelector/RadioGroup";
import { Button, H4, Icon } from "@sellar-io/components";
import { usePriceTierCreate } from "../../../hooks/api/v2/priceTier/usePriceTierCreate";
import { useNavigate } from "react-router";
import * as ROUTES from "../../../constants/routes";
import { usePriceTierConvertToBase } from "../../../hooks/api/v2/priceTier/usePriceTierConvertToBase";
import { NavigationBar } from "../../../components/navigation/NavigationBar";
import { twMerge } from "tailwind-merge";
import {
  PriceListGuidePages,
  PriceListGuideContext,
  usePriceListGuideContext,
} from "./PriceListGuide.shared";

export const PriceListGuide = ({ onDismiss }: { onDismiss: () => void }) => {
  const navigate = useNavigate();
  const [page, setPage] = useState<PriceListGuidePages>(
    PriceListGuidePages.SCREEN_1,
  );
  const [selectedPriceListId, setSelectedPriceListId] = useState<number>();

  const convertToBase = usePriceTierConvertToBase();

  const onConfirm = () => {
    if (!selectedPriceListId) {
      return;
    }
    convertToBase.mutate(
      {
        id: selectedPriceListId,
      },
      {
        onSuccess: (response) => {
          navigate(ROUTES.PRICE_LIST.href({ priceListId: response.id }));
        },
      },
    );
  };

  return (
    <PriceListGuideContext
      value={{
        page,
        setPage,
        selectedPriceListId,
        setSelectedPriceListId,
        onConfirm,
      }}
    >
      <NavigationBar
        title="Pricing Setup"
        leftIcon={page > 0 ? "CLOSE" : undefined}
        onClickLeftIcon={() => onDismiss()}
      />
      <div className="flex flex-col gap-8 px-4 py-8">
        {page === PriceListGuidePages.SCREEN_1 && (
          <PageContainer
            title="Welcome to your new Pricing area"
            description="We've reshaped how you can set up and manage your pricing structures on Sellar. Click 'Next' to see what's new."
          >
            <img
              className="w-full"
              width={896}
              height={390}
              src="https://sellar.imgix.net/sellar-image-assets/price-list-guide/screen-1.png"
              alt="instagram-logo"
            />
          </PageContainer>
        )}
        {page === PriceListGuidePages.SCREEN_2 && (
          <PageContainer
            title="Your old Price Tiers"
            description="Don't worry, all of the pricing you had set up in your Price Tiers has been moved across safely, and is still in use for your customers. You'll be able to view your old Price Tiers, and which customers are on them, but you won't be able to edit them. You can replace them with your new derived Price Lists at your own pace."
          >
            <img
              className="w-full"
              width={896}
              height={390}
              src="https://sellar.imgix.net/sellar-image-assets/price-list-guide/screen-2.png"
              alt="instagram-logo"
            />
          </PageContainer>
        )}
        {page === PriceListGuidePages.SCREEN_3 && (
          <PageContainer
            title="Your base price list"
            description="Your base price list is the starting point from which you can derive your custom price lists. A price must be entered against every product in your base price list."
          >
            <img
              className="w-full"
              width={896}
              height={390}
              src="https://sellar.imgix.net/sellar-image-assets/price-list-guide/screen-3.png"
              alt="instagram-logo"
            />
          </PageContainer>
        )}
        {page === PriceListGuidePages.SCREEN_4 && (
          <PageContainer
            title="Your derived price lists"
            description="Apply top-level discounts, per-item discounts and volume discounts in your derived price lists to create flexible custom pricing which you can then assign to your customers."
          >
            <img
              className="w-full"
              width={896}
              height={390}
              src="https://sellar.imgix.net/sellar-image-assets/price-list-guide/screen-4.png"
              alt="instagram-logo"
            />
          </PageContainer>
        )}
        {page === PriceListGuidePages.SCREEN_5 && (
          <PageContainer
            title="First things first, define your base price list"
            description="Save a bit of admin time by selecting one of your old price tiers to base your base price list on. Or, you can create one from scratch."
          >
            <ChooseBasePrice />
          </PageContainer>
        )}
        <Ctas />
      </div>
    </PriceListGuideContext>
  );
};

const ChooseBasePrice = () => {
  const navigate = useNavigate();
  const { selectedPriceListId, setSelectedPriceListId } =
    usePriceListGuideContext();

  const { data: priceLists } = usePriceTierList({
    query: [
      {
        type: "BasePriceTierId",
        modifier: "IS_NULL",
      },
      {
        type: "derivationTypeIncludes",
        value: ["custom"],
        modifier: "IN",
      },
    ],
  });

  const flatLists = useMemo(
    () => priceLists?.pages.flatMap((a) => a) ?? [],
    [priceLists],
  );

  useEffect(() => {
    if (flatLists.length > 0 && flatLists[0] && !selectedPriceListId) {
      setSelectedPriceListId(flatLists[0].id);
    }
  }, [flatLists, selectedPriceListId, setSelectedPriceListId]);

  const createBasePriceList = usePriceTierCreate();

  const onCreate = () => {
    createBasePriceList.mutate(
      {
        name: "Base list",
        derivationType: "base",
      },
      {
        onSuccess: (response) => {
          navigate(ROUTES.PRICE_LIST.href({ priceListId: response.id }), {
            state: { isNewBase: true },
          });
        },
      },
    );
  };

  return (
    <div className="flex w-full justify-center bg-zinc-100 px-4 py-8">
      <div className="flex w-full max-w-2xl flex-col gap-4">
        <div className="flex flex-col gap-2">
          <InputLabel>Select price list</InputLabel>
          <RadioGroup
            value={selectedPriceListId?.toString()}
            onChange={(value) => setSelectedPriceListId(parseInt(value))}
          >
            <RadioGroupContainer className="divide-y-0 border-0">
              {flatLists.map((pl, idx) => (
                <RadioGroupOption
                  key={pl.id}
                  value={pl.id.toString()}
                  className={twMerge(
                    selectedPriceListId === pl.id ? "bg-zinc-200" : "bg-white",
                    "border border-zinc-300 font-semibold ring-0",
                    idx > 0 ? "border-t-0" : "",
                  )}
                >
                  {pl.name}
                </RadioGroupOption>
              ))}
            </RadioGroupContainer>
          </RadioGroup>
        </div>
        <div className="flex flex-col gap-2">
          <InputLabel>Or define a new base price list</InputLabel>
          <Button
            color="secondary"
            className="flex w-full items-center gap-2"
            loading={createBasePriceList.isPending}
            onClick={onCreate}
          >
            <Icon icon="PLUS" />
            CREATE NEW BASE PRICE LIST
          </Button>
        </div>
      </div>
    </div>
  );
};

const PageContainer = ({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) => {
  return (
    <div className="mx-auto flex max-w-4xl flex-col overflow-hidden rounded-lg bg-zinc-300 shadow-[0px_16px_32px_0px_rgba(0,0,0,0.16)]">
      <div className="flex flex-col gap-4 bg-white p-8">
        <H4>{title}</H4>
        <p>{description}</p>
      </div>
      <div>{children}</div>
    </div>
  );
};

const Ctas = () => {
  const { page, setPage, onConfirm } = usePriceListGuideContext();

  return (
    <div className="mx-auto flex w-full max-w-4xl justify-end gap-4">
      {page > 0 && (
        <Button
          color="secondary"
          className="w-1/2"
          onClick={() => setPage(page - 1)}
        >
          Back
        </Button>
      )}
      <Button
        className="w-1/2"
        onClick={() => {
          if (page === 4) {
            onConfirm();
          } else {
            setPage(page + 1);
          }
        }}
      >
        {page === 4 ? "Confirm" : "Next"}
      </Button>
    </div>
  );
};
